import Button from 'components/UI/Buttons/Button'
import Document from 'components/UI/Document'
import Typography from 'components/UI/Typography'
import { useAppSelector } from 'hooks'
import { Fragment, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLazyGetApplicationStatusQuery } from 'redux/api'
import { useGetUnsignedDocumentsQuery } from 'redux/api/creditApi'
import { useFinishAcquaintanceStepMutation } from 'redux/api/pledgeApi'
import { getCreditParamsSelector, getCurrentFlowType, getStatusSelector } from 'redux/store/slices/statusSlice'
import { getCurrentFlowId } from 'redux/store/slices/userSlice'
import { getNotificationsSelector } from 'redux/store/slices/wsSlice'
import { ApplicationEvents } from 'share/enums/events'
import { FLOW_STEPS } from 'share/types'
import tw, { TwStyle } from 'twin.macro'
import { downloadFileWithFetch, getWsStepStatus } from 'utils'
import { pathnames } from 'utils/constants'
import Emitter from 'utils/emitter'

import BlockchaingRegisterState from './BlockchaingRegisterState'

type DocumentsListProps = {
  twStyle?: TwStyle
}

const DocumentsList = ({ twStyle }: DocumentsListProps) => {
  // Tools
  const navigate = useNavigate()
  const {
    t,
    i18n: { language }
  } = useTranslation()

  // Selectors
  const { pay_day } = useAppSelector(getCreditParamsSelector)
  const currentFlow = useAppSelector(getCurrentFlowType)
  const currentFlowId = useAppSelector(getCurrentFlowId)
  const { current_step, next_step: nextStep } = useAppSelector(getStatusSelector)
  const { BLOCKCHAIN_CREATE_RECORD } = useAppSelector(getNotificationsSelector)

  // Queries and Mutations
  const { data: agreementDocuments, isLoading: isAgreementsLoading } = useGetUnsignedDocumentsQuery(['agreements'])
  const { data: pledgeAgreementDocuments, isLoading: isPledgeAgreementsLoading } = useGetUnsignedDocumentsQuery(
    ['pledge_agreement'],
    {
      skip: currentFlow !== 'THIRD_PARTY_PLEDGE'
    }
  )
  const { data: otherDocuments, isLoading: isOtherDocsLoading } = useGetUnsignedDocumentsQuery(['assignments'])
  const { data: repaymentScheduleDocuments, isLoading: isRepSchDocLoading } = useGetUnsignedDocumentsQuery([
    'repayment_schedule'
  ])
  const { data: insuranceDocuments, isLoading: isInsDocLoading } = useGetUnsignedDocumentsQuery([
    'borrower_insurance_doc'
  ])
  const { data: insurancePaymentDocuments, isLoading: isInsPaysmentDocLoading } = useGetUnsignedDocumentsQuery([
    'insurance_payment_doc'
  ])
  const { data: damuDocuments, isLoading: isDamuDocsLoading } = useGetUnsignedDocumentsQuery(['notification_docs'])

  const { data: applicationDocuments, isLoading: isApplicationLoading } = useGetUnsignedDocumentsQuery([
    'client_applications'
  ])

  const [fetchStatus, { isLoading: isStatusLoading }] = useLazyGetApplicationStatusQuery()
  const [finishAcquaintanceStep] = useFinishAcquaintanceStepMutation()

  // Computed values
  const documentsList = useMemo(() => {
    return [
      { title: t('flow-contracts'), data: [agreementDocuments, pledgeAgreementDocuments] },
      { title: t('flow-orders'), data: otherDocuments },
      { title: t('flow-schedule'), data: repaymentScheduleDocuments },
      { title: t('flow-application-document-insurance'), data: [insuranceDocuments, insurancePaymentDocuments] },
      { title: t('flow-damu-notification-doc'), data: damuDocuments },
      { title: t('flow-application-statement'), data: applicationDocuments }
    ]
  }, [
    agreementDocuments,
    damuDocuments,
    insuranceDocuments,
    insurancePaymentDocuments,
    otherDocuments,
    pledgeAgreementDocuments,
    repaymentScheduleDocuments,
    applicationDocuments,
    t
  ])
  const isBtnDisabled = !pay_day ? (nextStep || current_step?.step) !== FLOW_STEPS.SIGN_DOCS_AITU : false
  const isCurrentStepBlockchain = (nextStep || current_step?.step) === FLOW_STEPS.BLOCKCHAIN_CREATE_RECORD
  const blockchainStepStatus = getWsStepStatus(BLOCKCHAIN_CREATE_RECORD)

  // Handlers
  const goToNextStepPage = async () => {
    if (!currentFlowId) return
    const response = await fetchStatus(currentFlowId).unwrap()
    const { next_step, current_step } = response
    if (!current_step) return
    const { id, step } = current_step
    if (next_step === FLOW_STEPS.ACQUAINTANCE_WITH_DOCUMENTS || step === FLOW_STEPS.ACQUAINTANCE_WITH_DOCUMENTS) {
      await finishAcquaintanceStep('' + id).unwrap()
      Emitter.emit(ApplicationEvents.NAVIGATE_TO_NEXT_PAGE)
    }
    navigate(pathnames.biometry)
  }

  return (
    <Fragment>
      <div css={[tw`space-y-10`, twStyle]}>
        {documentsList.map(({ title, data }, idx) => {
          if (!data || data.length === 0) return
          return (
            <div key={idx} tw='space-y-3'>
              <Typography weight='bold'>{title}</Typography>
              {data?.map(documents => {
                if (Array.isArray(documents)) {
                  return documents.map((doc, idx) => {
                    return (
                      <Document
                        key={idx}
                        title={language === 'kk' ? doc.document_type_verbose_kk : doc.document_type_verbose}
                        onDownload={() => downloadFileWithFetch(doc.id, doc.document_type_verbose)}
                      />
                    )
                  })
                } else if (documents) {
                  return (
                    <Document
                      key={documents.id}
                      title={language === 'kk' ? documents.document_type_verbose_kk : documents.document_type_verbose}
                      onDownload={() => downloadFileWithFetch(documents.id, documents.document_type_verbose)}
                    />
                  )
                } else {
                  return null
                }
              })}
            </div>
          )
        })}
      </div>
      {isCurrentStepBlockchain || blockchainStepStatus === 'IN_PROGRESS' ? (
        <BlockchaingRegisterState stepStatus={blockchainStepStatus} twStyle={tw`mt-4`} />
      ) : (
        <Button
          variant='primary'
          twStyle={tw`mt-[52px] md:mt-8 self-end`}
          disabled={isBtnDisabled}
          loading={
            isAgreementsLoading ||
            isRepSchDocLoading ||
            isOtherDocsLoading ||
            isInsDocLoading ||
            isPledgeAgreementsLoading ||
            isInsPaysmentDocLoading ||
            isDamuDocsLoading ||
            isApplicationLoading ||
            isStatusLoading
          }
          onClick={goToNextStepPage}
        >
          <Fragment>
            <Typography variant='body' weight='bold' twStyle={tw`text-white`}>
              {t('flow-online-sign')}
            </Typography>
            <Typography variant='caption' twStyle={tw`text-white`}>
              {t('flow-check-identification')}
            </Typography>
          </Fragment>
        </Button>
      )}
    </Fragment>
  )
}

export default DocumentsList
